import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Clapperboard,
  File,
  FileAudio,
  FileImage,
  FileVideo,
  MonitorPlay,
  Upload,
  Wand2,
  X,
  Youtube,
} from 'lucide-react';
import './ScratchVideoTemplatePage.css';
import UniversalLayout from './components/UniversalLayout';
import { useAuthContext } from './components/AuthProvider';
import AuthModal from './components/AuthModal';
import AgentAutoReloadModal from './components/AgentAutoReloadModal';
import {
  getAgentEligibility,
  initializeProject,
  startAgentCompose,
  updateCreditSettings,
} from './lib/api';

const TEMPLATE_CONFIGS = {
  'zack-d-style-shorts': {
    title: 'Zack D. Style Shorts',
    titlePrefix: 'Zack D.',
    titleEm: 'Style Shorts',
    icon: Clapperboard,
    aspectRatio: '9:16',
    modelTier: 'pro',
    sourceTag: 'zack_d_style_shorts_template',
    placeholder: 'Describe the short you want: topic, target audience, core point, tone, offer, and any scenes or assets to include.',
    subtitle: 'Create short-form creator-style videos from a brief and optional media assets.',
    whatThisDoes: 'This template turns a description into a vertical short with a strong opening beat, punchy pacing, direct narration, and asset-aware visuals.',
    steps: ['Write brief', 'Attach assets', 'Generate short'],
    buildPrompt: (description) => `Create a vertical short-form video from scratch. Use the zack-d-style-shorts skill.

User brief:
${description}

Use any attached images, videos, or audio as source material, references, product visuals, examples, or voice/music assets when relevant.

Creative direction:
- Build a 9:16 short with a fast first beat, clear thesis, concise narration, and high-retention pacing.
- Structure it like a punchy creator short: hook, rapid context, key insight or reveal, proof/example, and clean ending.
- Use bold but credible on-screen text, tight cuts, kinetic pacing, and visual variety.
- If product or brand assets are attached, incorporate them naturally without making the video feel like a static slideshow.
- Keep claims grounded in the user brief and attached assets.
- Return a complete editable video project.`,
  },
  'fern-style-youtube-video': {
    title: 'Fern Style YouTube Video',
    titlePrefix: 'Fern Style',
    titleEm: 'YouTube Video',
    icon: Youtube,
    aspectRatio: '16:9',
    modelTier: 'pro',
    sourceTag: 'fern_style_youtube_video_template',
    placeholder: 'Describe the YouTube video: topic, thesis, key story beats, audience, tone, references, and assets you want used.',
    subtitle: 'Create a cinematic YouTube explainer-style video from a description and optional media assets.',
    whatThisDoes: 'This template creates a widescreen narrative video with documentary pacing, strong visual structure, and asset-aware exposition.',
    steps: ['Write thesis', 'Attach media', 'Generate video'],
    buildPrompt: (description) => `Create a widescreen YouTube video from scratch. Use the fern-style-youtube-video skill.

User brief:
${description}

Use any attached images, videos, or audio as source material, references, archival-style media, product visuals, examples, voice, or music when relevant.

Creative direction:
- Build a 16:9 cinematic explainer with a clear thesis, narrative escalation, and polished editorial pacing.
- Use a documentary-style structure: intriguing opening, context, key turns, evidence/examples, synthesis, and memorable ending.
- Emphasize clean motion graphics, tasteful typography, map/data/interface-style visuals when useful, and cinematic transitions.
- Keep narration precise and measured. Do not overstate facts beyond the brief or attached assets.
- Make the visual language cohesive across the full video.
- Return a complete editable video project.`,
  },
  'saas-product-demo': {
    title: 'SaaS Product Demo',
    titlePrefix: 'SaaS Product',
    titleEm: 'Demo',
    icon: MonitorPlay,
    aspectRatio: '16:9',
    modelTier: 'pro',
    sourceTag: 'saas_product_demo_template',
    placeholder: 'Describe the product, user pain, target customer, key feature flow, desired CTA, and any screenshots or recordings to use.',
    subtitle: 'Create a polished SaaS product walkthrough from a brief, screenshots, videos, or audio.',
    whatThisDoes: 'This template turns product context and optional assets into a clear demo video with problem framing, feature walkthrough, and CTA.',
    steps: ['Describe product', 'Attach assets', 'Generate demo'],
    buildPrompt: (description) => `Create a SaaS product demo video from scratch. Use the saas-product-demo skill.

User brief:
${description}

Use any attached screenshots, screen recordings, product videos, images, or audio as product source material and visual references.

Creative direction:
- Build a polished 16:9 SaaS demo with a clear problem, product promise, feature walkthrough, benefit moments, and CTA.
- If product UI assets are attached, use them accurately and prioritize showing the real workflow over generic marketing visuals.
- Use clean interface zooms, cursor/highlight moments, callouts, captions, and restrained motion graphics.
- Keep the tone direct, credible, and product-led.
- Do not invent features, integrations, metrics, pricing, discounts, or guarantees not present in the brief or assets.
- Return a complete editable video project.`,
  },
};

function formatBytes(bytes) {
  if (!bytes) return '0 KB';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getAssetType(file) {
  if (file.type?.startsWith('image/')) return 'image';
  if (file.type?.startsWith('video/')) return 'video';
  if (file.type?.startsWith('audio/')) return 'audio';
  return 'file';
}

function getAssetIcon(type) {
  if (type === 'image') return FileImage;
  if (type === 'video') return FileVideo;
  if (type === 'audio') return FileAudio;
  return File;
}

function ScratchVideoTemplatePage({ templateId }) {
  const config = TEMPLATE_CONFIGS[templateId] || TEMPLATE_CONFIGS['saas-product-demo'];
  const Icon = config.icon;
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const pendingSubmitRef = useRef(false);
  const {
    user, loading, openAuthModal, closeAuthModal, authModalOpen,
    signInWithGoogle, signInWithEmail,
  } = useAuthContext();

  const [description, setDescription] = useState('');
  const [assetFiles, setAssetFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [showAutoReloadModal, setShowAutoReloadModal] = useState(false);

  const canSubmit = description.trim().length > 0 && !isSubmitting;
  const hasVideoAssets = assetFiles.some((file) => getAssetType(file) === 'video');

  useEffect(() => {
    pendingSubmitRef.current = false;
  }, [templateId]);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    setAssetFiles((prev) => [...prev, ...files]);
    setSubmitError(null);
    event.target.value = '';
  };

  const handleRemoveAsset = (index) => {
    setAssetFiles((prev) => prev.filter((_, idx) => idx !== index));
  };

  const createProjectFromTemplate = useCallback(async () => {
    const cleanDescription = description.trim();
    if (!cleanDescription) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const eligibility = await getAgentEligibility();
      if (eligibility && !eligibility.allowed && eligibility.reason === 'auto_reload_required') {
        setShowAutoReloadModal(true);
        setIsSubmitting(false);
        return;
      }
    } catch (err) {
      console.error('Eligibility check failed:', err);
    }

    try {
      const projectId = Math.random().toString(36).substring(2, 15);
      const prompt = config.buildPrompt(cleanDescription);
      await initializeProject(projectId, prompt, config.aspectRatio);
      await startAgentCompose(
        projectId,
        prompt,
        assetFiles,
        config.modelTier,
        [],
        config.sourceTag,
        { analyzeVideoAttachments: hasVideoAssets }
      );

      navigate(`/project/${projectId}`, {
        state: { agentMode: true, promptText: prompt },
      });
    } catch (err) {
      console.error(`${config.title} generation failed:`, err);
      setSubmitError('Failed to start generation. Please try again.');
      setIsSubmitting(false);
    }
  }, [assetFiles, config, description, hasVideoAssets, navigate]);

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) return;

    if (!user && !loading) {
      pendingSubmitRef.current = true;
      openAuthModal();
      return;
    }

    await createProjectFromTemplate();
  }, [canSubmit, createProjectFromTemplate, loading, openAuthModal, user]);

  const handleEnableAutoReloadAndContinue = useCallback(async ({ thresholdCents, amountCents }) => {
    await updateCreditSettings({
      autoReloadEnabled: true,
      autoReloadThresholdCents: thresholdCents,
      autoReloadAmountCents: amountCents,
    });
    setShowAutoReloadModal(false);
    await createProjectFromTemplate();
  }, [createProjectFromTemplate]);

  useEffect(() => {
    if (!user || !pendingSubmitRef.current) return;
    pendingSubmitRef.current = false;
    createProjectFromTemplate();
  }, [createProjectFromTemplate, user]);

  return (
    <UniversalLayout>
      <div className="scratch-content">
        <main className="scratch-main">
          <header className="scratch-header">
            <span className="scratch-eyebrow">Template</span>
            <h1 className="scratch-title">
              {config.titlePrefix} <span>{config.titleEm}</span>
            </h1>
            <p className="scratch-subtitle">{config.subtitle}</p>
          </header>

          <div className="scratch-grid">
            <section className="scratch-panel scratch-panel--form">
              <div className="scratch-section-header">
                <span>01</span>
                <div>
                  <h2>Description</h2>
                  <p>This is required. Assets below are optional.</p>
                </div>
              </div>

              <textarea
                className="scratch-description"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder={config.placeholder}
                rows={7}
              />

              <div className="scratch-section-header scratch-section-header--assets">
                <span>02</span>
                <div>
                  <h2>Assets</h2>
                  <p>Attach images, videos, or audio for the agent to use.</p>
                </div>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*,audio/*"
                multiple
                className="scratch-file-input"
                onChange={handleFileChange}
              />

              <button type="button" className="scratch-upload-zone" onClick={handleUploadClick}>
                <Upload size={21} strokeWidth={1.7} />
                <span>Add Assets</span>
                <small>Images, videos, or audio</small>
              </button>

              {assetFiles.length > 0 && (
                <div className="scratch-assets-list">
                  {assetFiles.map((file, index) => {
                    const type = getAssetType(file);
                    const AssetIcon = getAssetIcon(type);
                    return (
                      <div key={`${file.name}-${index}`} className="scratch-asset-row">
                        <span className={`scratch-asset-icon scratch-asset-icon--${type}`} aria-hidden="true">
                          <AssetIcon size={17} strokeWidth={1.8} />
                        </span>
                        <span className="scratch-asset-meta">
                          <strong>{file.name}</strong>
                          <small>{type} · {formatBytes(file.size)}</small>
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveAsset(index)}
                          aria-label={`Remove ${file.name}`}
                        >
                          <X size={15} strokeWidth={1.9} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              <button
                type="button"
                className="scratch-submit"
                disabled={!canSubmit}
                onClick={handleSubmit}
              >
                {isSubmitting ? (
                  <>
                    <span className="scratch-spinner" />
                    Starting generation
                  </>
                ) : (
                  <>
                    <Wand2 size={17} strokeWidth={1.9} />
                    Create Video
                  </>
                )}
              </button>

              {submitError && <span className="scratch-error">{submitError}</span>}
            </section>

            <aside className="scratch-panel scratch-panel--aside">
              <div className="scratch-aside-icon" aria-hidden="true">
                <Icon size={23} strokeWidth={1.7} />
              </div>
              <h2>What this creates</h2>
              <p>{config.whatThisDoes}</p>
              <div className="scratch-aside-steps">
                {config.steps.map((step) => (
                  <span key={step}>{step}</span>
                ))}
              </div>
            </aside>
          </div>
        </main>
      </div>

      <AuthModal
        isOpen={!loading && !user && authModalOpen}
        onClose={closeAuthModal}
        onSignIn={() => signInWithGoogle()}
        onEmailSignIn={signInWithEmail}
      />

      <AgentAutoReloadModal
        isOpen={showAutoReloadModal}
        onClose={() => setShowAutoReloadModal(false)}
        onEnableAutoReload={handleEnableAutoReloadAndContinue}
        onCreateBlankProject={null}
      />
    </UniversalLayout>
  );
}

export default ScratchVideoTemplatePage;
